import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Crown,
  Package,
  ShoppingCart,
  Users,
  Truck,
  Wallet,
  BarChart3,
  Activity,
  Megaphone,
  UserCog,
  CreditCard,
  TruckIcon,
  QrCode,
  ArrowLeft,
  Sparkles,
  CheckCircle,
  Star,
} from "lucide-react";

export const WelcomeModal = () => {
  const { user, userPlan, isTrial } = useAuth();
  const language = "ar";
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  
  const isArabic = language === "ar";
  
  useEffect(() => {
    // التحقق من أنه أول تسجيل دخول
    const hasSeenWelcome = localStorage.getItem(`welcome_seen_${user?.id}`);
    // عرض الرسالة للمستخدمين في الفترة التجريبية أو الباقة الأساسية
    if (user && !hasSeenWelcome && (isTrial || userPlan === "basic")) {
      setIsOpen(true);
    }
  }, [user, isTrial, userPlan]);
  
  const handleClose = () => {
    if (user?.id) {
      localStorage.setItem(`welcome_seen_${user.id}`, "true");
    }
    setIsOpen(false);
  };
  
  const handleUpgrade = () => {
    handleClose();
    navigate("/subscription");
  };
  
  const basicFeatures = [
    { icon: ShoppingCart, label: isArabic ? "نقطة البيع" : "Point of Sale" },
    { icon: Package, label: isArabic ? "المخزون" : "Inventory" },
    { icon: Users, label: isArabic ? "العملاء" : "Customers" },
    { icon: Truck, label: isArabic ? "الموردين" : "Suppliers" },
    { icon: Wallet, label: isArabic ? "المصروفات" : "Expenses" },
    { icon: BarChart3, label: isArabic ? "التقارير" : "Reports" },
  ];
  
  const proFeatures = [
    { icon: Activity, label: isArabic ? "لوحة مباشرة" : "Live Dashboard" },
    { icon: CreditCard, label: isArabic ? "ديون الزبائن" : "Customer Debts" },
    { icon: UserCog, label: isArabic ? "الموظفين" : "Employees" },
    { icon: TruckIcon, label: isArabic ? "التوصيل" : "Delivery" },
    { icon: Megaphone, label: isArabic ? "التسويق" : "Marketing" },
    { icon: QrCode, label: isArabic ? "صفحة المتجر" : "Store Page" },
  ];
  
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="bg-gradient-to-b from-slate-900 to-slate-950 border-slate-700 text-white max-w-lg p-0 overflow-hidden">
        {step === 1 ? (
          <>
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <DialogTitle className="text-2xl font-bold text-white mb-2">
                {isArabic ? "مرحباً بك في نازاريو!" : "Welcome to Nazario!"}
              </DialogTitle>
              <p className="text-emerald-100 text-sm">
                {isArabic 
                  ? `أهلاً ${user?.name || ""}! لديك 5 أيام تجريبية مع جميع الميزات`
                  : `Hi ${user?.name || ""}! You have 5 days trial with all features`
                }
              </p>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Trial Info */}
              <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
                <div className="flex items-center gap-3">
                  <Star className="w-8 h-8 text-amber-400" />
                  <div>
                    <p className="text-amber-400 font-bold">
                      {isArabic ? "فترة تجريبية مجانية" : "Free Trial Period"}
                    </p>
                    <p className="text-slate-400 text-sm">
                      {isArabic 
                        ? "استمتع بجميع الميزات الاحترافية لمدة 5 أيام"
                        : "Enjoy all professional features for 5 days"
                      }
                    </p>
                  </div>
                </div>
              </div>
              
              {/* What you can do */}
              <div>
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                  {isArabic ? "ما يمكنك فعله الآن:" : "What you can do now:"}
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {[...basicFeatures, ...proFeatures.slice(0, 2)].map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-slate-800/50 text-sm">
                      <feature.icon className="w-4 h-4 text-emerald-400" />
                      <span className="text-slate-300">{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setStep(2)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                >
                  {isArabic ? "اعرف المزيد عن الباقات" : "Learn about plans"}
                  <ArrowLeft className="w-4 h-4 ms-2" />
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  {isArabic ? "ابدأ الآن" : "Start Now"}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Plans Comparison */}
            <DialogHeader className="p-6 pb-2">
              <DialogTitle className="text-xl font-bold text-white text-center">
                {isArabic ? "اختر الباقة المناسبة لك" : "Choose the right plan for you"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="p-6 pt-2 space-y-4">
              {/* Unified Plan */}
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30 relative">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-0.5 bg-emerald-500 text-white text-xs font-bold rounded-full">
                    {isArabic ? "سعر موحد" : "Single Price"}
                  </span>
                </div>
                <div className="flex items-center justify-between mb-3 mt-1">
                  <div className="flex items-center gap-2">
                    <Crown className="w-6 h-6 text-emerald-400" />
                    <span className="text-emerald-400 font-bold">
                      {isArabic ? "الترخيص الدائم" : "Lifetime License"}
                    </span>
                  </div>
                  <span className="text-emerald-400 font-bold">
                    690,000 د.ع
                  </span>
                </div>
                <p className="text-slate-400 text-sm mb-2">
                  {isArabic ? "جميع الميزات - دفعة واحدة فقط" : "All features - One time payment"}
                </p>
                <div className="flex flex-wrap gap-1">
                  {basicFeatures.map((f, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded">
                      {f.label}
                    </span>
                  ))}
                  {proFeatures.map((f, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded">
                      {f.label}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleUpgrade}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                >
                  <Crown className="w-4 h-4 me-2" />
                  {isArabic ? "اشترك الآن" : "Subscribe Now"}
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  className="border-slate-600 text-slate-300"
                >
                  {isArabic ? "لاحقاً" : "Later"}
                </Button>
              </div>
              
              <p className="text-center text-slate-500 text-xs">
                {isArabic 
                  ? "يمكنك الترقية في أي وقت من صفحة الاشتراك"
                  : "You can upgrade anytime from the subscription page"
                }
              </p>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
